import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { BellIcon, LogOutIcon, SearchIcon, UserIcon } from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  ThemeToggle,
} from '@opengoal/ui';
import { useTranslation } from 'react-i18next';
import { useCurrentUser, useSignOut } from '@/modules/auth';
import { initials } from '@/shared/lib/format';
import {
  CURRENCY_SYMBOLS,
  DISPLAY_CURRENCIES,
  useDisplayCurrency,
  type DisplayCurrency,
} from './currency';

/**
 * Everything on the trailing edge of the header, in the order the design
 * shows it: search, currency, theme, notifications, then the account.
 */
export function HeaderControls() {
  return (
    <>
      <GlobalSearch />
      <CurrencySelect />
      <ThemeToggle />
      <NotificationsButton />
      <Separator orientation="vertical" className="mx-1 hidden h-6! sm:block" />
      <UserMenu />
    </>
  );
}

/**
 * Searches the roster: Enter opens the players list filtered by the text.
 * Players are the only searchable collection today; widening this to clubs
 * and contracts needs a backend search endpoint (see docs/PRODUCTION.md).
 */
function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [value, setValue] = useState('');

  return (
    <form
      role="search"
      className="relative hidden lg:block"
      onSubmit={(event) => {
        event.preventDefault();
        const search = value.trim();
        void navigate({
          to: '/players',
          search: (prev) => ({ ...prev, search: search || undefined, page: 1 }),
        });
      }}
    >
      <SearchIcon className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        type="search"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder={t('header.searchPlaceholder')}
        aria-label={t('header.searchPlaceholder')}
        className="w-64 ps-9"
      />
    </form>
  );
}

function CurrencySelect() {
  const { t } = useTranslation();
  const currency = useDisplayCurrency((state) => state.currency);
  const setCurrency = useDisplayCurrency((state) => state.setCurrency);

  return (
    <Select value={currency} onValueChange={(value) => setCurrency(value as DisplayCurrency)}>
      <SelectTrigger className="hidden w-[6.25rem] sm:flex" aria-label={t('header.currency')}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end">
        {DISPLAY_CURRENCIES.map((code) => (
          <SelectItem key={code} value={code}>
            {CURRENCY_SYMBOLS[code]} {code}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * The bell. There is no notifications feed on the backend yet, so the count is
 * always zero and the panel explains that rather than faking a list.
 */
function NotificationsButton() {
  const { t } = useTranslation();
  const unread = 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={t('header.notifications')}
        >
          <BellIcon />
          {unread > 0 ? (
            <span className="absolute end-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] leading-none font-semibold text-white">
              {unread}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="border-b px-4 py-3 text-sm font-semibold">{t('header.notifications')}</div>
        <EmptyState
          icon={<BellIcon />}
          title={t('header.noNotifications')}
          description={t('header.noNotificationsHint')}
        />
      </PopoverContent>
    </Popover>
  );
}

function UserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const signOut = useSignOut();

  const name = user?.name ?? user?.email ?? t('header.account');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 rounded-full py-0.5 ps-0.5 pe-1 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 sm:pe-2"
        >
          <Avatar className="size-9">
            <AvatarFallback className="bg-primary text-[13px] text-primary-foreground">
              {initials(name)}
            </AvatarFallback>
          </Avatar>
          <span className="hidden max-w-40 truncate text-[13px] font-medium sm:block">{name}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="grid gap-0.5">
          <span className="truncate">{user?.name ?? t('header.account')}</span>
          {user?.email ? (
            <span className="truncate text-xs font-normal text-muted-foreground">{user.email}</span>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void navigate({ to: '/settings' })}>
          <UserIcon />
          {t('profile.myProfile')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={() => void signOut()}>
          <LogOutIcon />
          {t('auth.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
